/**
 * "Velja nótur": the sheet choice of the open song, opened by tapping the active song tab when the
 * song has more than one sheet (it replaces the old chip row under the top bar). Large radio rows
 * (≥ 48 px) with the audience dot; a sheet for another section has a dashed outline (as the chips
 * had). Choosing closes the menu; the song page remembers the choice in the tab and on the device.
 */
import { Check } from 'lucide-react';
import { matchesOwnAudience } from '../../shared/audience';
import type { SongFileDTO } from '../../shared/dto/library';
import { TrackDot } from '../audio/AudioControls';
import { useMe } from '../auth/AuthProvider';
import { cx, Sheet } from '../components/ui';
import { useT } from '../i18n';

/** The open song's visible sheets and the one showing. */
export interface SheetChoice {
  files: SongFileDTO[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

/** The sheets as a column of radio rows (also at the top of the list drawer). */
export function SheetChoices({ choice, onChosen }: { choice: SheetChoice; onChosen?: () => void }) {
  const t = useT();
  const me = useMe();
  return (
    <div role="radiogroup" aria-label={t('viewer.sheets_label')} className="flex flex-col gap-2">
      {choice.files.map((f) => {
        const on = f.id === choice.selectedId;
        const foreign = !!me && !matchesOwnAudience(f.audienceMask, me.section);
        return (
          <button
            key={f.id}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => {
              if (!on) choice.onSelect(f.id);
              onChosen?.();
            }}
            className={cx(
              'flex min-h-12 w-full items-center gap-3 rounded-2xl border px-4 py-2 text-left text-[17px] font-semibold transition active:scale-[0.99]',
              on ? 'border-transparent bg-[var(--accent)] text-[var(--accent-ink)]' : 'border-[var(--line)] bg-[var(--surface)] text-[var(--ink)] hover:bg-[var(--surface-2)]',
              foreign && !on && 'border-dashed',
            )}
          >
            <TrackDot track={f} />
            <span className="min-w-0 flex-1 break-words">{f.title}</span>
            {on ? <Check className="size-5 shrink-0" strokeWidth={3} aria-hidden /> : null}
          </button>
        );
      })}
    </div>
  );
}

export function SheetMenu({ open, onClose, choice }: { open: boolean; onClose: () => void; choice: SheetChoice | null }) {
  const t = useT();
  return (
    <Sheet open={open && !!choice} onClose={onClose} title={t('tabs.choose_sheet')}>
      {choice ? (
        <div className="pt-1 pb-2">
          <SheetChoices choice={choice} onChosen={onClose} />
        </div>
      ) : null}
    </Sheet>
  );
}
